import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function AuthCompletePage() {
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { completeEmailLinkSignIn, isSignInWithEmailLink } = useAuth();

  useEffect(() => {
    const handleSignIn = async () => {
      // Check if this is actually a sign-in link
      if (!isSignInWithEmailLink()) {
        setStatus('error');
        setError('Invalid sign-in link. Please request a new one.');
        return;
      }

      try {
        // Try to get email from localStorage first
        let userEmail = window.localStorage.getItem('emailForSignIn');
        
        // If no email in storage, prompt user (different device scenario)
        if (!userEmail) {
          userEmail = window.prompt('Please provide your email for confirmation:');
          if (!userEmail) {
            setStatus('error');
            setError('Email is required to complete sign-in.');
            return;
          }
        }

        setEmail(userEmail);
        
        // Complete the sign-in
        await completeEmailLinkSignIn(userEmail);
        setStatus('success');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
        
      } catch (error) {
        console.error('Email link sign-in error:', error);
        setStatus('error');
        setError(error.message || 'Failed to complete sign-in. Please try again.');
      }
    };

    handleSignIn();
  }, [completeEmailLinkSignIn, isSignInWithEmailLink, navigate]);

  const handleRetry = () => {
    navigate('/auth/signin');
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-500 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Completing sign-in...
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we verify your email link
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-orange-50">
        <div className="text-center max-w-md w-full mx-4">
          <CheckCircleIcon className="mx-auto h-24 w-24 text-green-500" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome to PFI Connect!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Successfully signed in as <span className="font-medium text-pink-600">{email}</span>
          </p>
          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-800">
              Redirecting to your dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-orange-50">
        <div className="text-center max-w-md w-full mx-4">
          <ExclamationTriangleIcon className="mx-auto h-24 w-24 text-red-500" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Sign-in Failed
          </h2>
          <div className="mt-4 p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <div className="mt-6 space-y-3">
            <button
              onClick={handleRetry}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200"
            >
              Request new sign-in link
            </button>
            <p className="text-xs text-gray-500">
              Links expire after 1 hour for security
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}